type EventSignature = string;

interface EventRecord {
  signature: EventSignature;
  timestamp: number;
}

const DEDUPLICATION_WINDOW_MS = 2000;

const recentEvents: Map<EventSignature, number> = new Map();

let cleanupInterval: NodeJS.Timeout | null = null;

function startCleanupInterval() {
  if (cleanupInterval) return;

  cleanupInterval = setInterval(() => {
    const now = Date.now();
    const expiredKeys: string[] = [];

    recentEvents.forEach((timestamp, signature) => {
      if (now - timestamp > DEDUPLICATION_WINDOW_MS * 2) {
        expiredKeys.push(signature);
      }
    });

    expiredKeys.forEach(key => recentEvents.delete(key));

    if (recentEvents.size === 0 && cleanupInterval) {
      clearInterval(cleanupInterval);
      cleanupInterval = null;
    }
  }, 5000);
}

function createEventSignature(eventType: string, pageName: string, pageUrl?: string): EventSignature {
  return `${eventType}:${pageName}:${pageUrl || ''}`;
}

export function shouldTrackEvent(
  eventType: string,
  pageName: string,
  pageUrl?: string,
  customWindow?: number
): boolean {
  const signature = createEventSignature(eventType, pageName, pageUrl);
  const now = Date.now();
  const lastTracked = recentEvents.get(signature);

  const windowMs = customWindow ?? DEDUPLICATION_WINDOW_MS;

  if (lastTracked && now - lastTracked < windowMs) {
    console.log(
      `[EventDedup] 🚫 Blocking duplicate ${eventType} for "${pageName}" ` +
      `(${Math.round((now - lastTracked) / 1000)}s since last)`
    );
    return false;
  }

  recentEvents.set(signature, now);
  startCleanupInterval();

  console.log(`[EventDedup] ✅ Allowing ${eventType} for "${pageName}"`);
  return true;
}

export function clearEventCache() {
  recentEvents.clear();
  if (cleanupInterval) {
    clearInterval(cleanupInterval);
    cleanupInterval = null;
  }
  console.log('[EventDedup] Cache cleared');
}

export function getEventCacheSize(): number {
  return recentEvents.size;
}

export function isRecentEvent(
  eventType: string,
  pageName: string,
  pageUrl?: string,
  windowMs: number = DEDUPLICATION_WINDOW_MS
): boolean {
  const signature = createEventSignature(eventType, pageName, pageUrl);
  const lastTracked = recentEvents.get(signature);

  if (!lastTracked) return false;

  const now = Date.now();
  return now - lastTracked < windowMs;
}
